import { zodResolver } from "@hookform/resolvers/zod";
import type { Employee, ManagerAssessment, PerformanceCycle } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Calendar, Search, TrendingDown, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { formatDate, PERFORMANCE_RATINGS, parseLocalDate } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { matchesSearch } from "@/lib/search-utils";

type ExtendedPerformanceCycle = PerformanceCycle & {
  employee?: Employee;
  managerAssessments?: ManagerAssessment[];
};

const assessmentFormSchema = z.object({
  ratingFinal: z.string().min(1, "Rating is required"),
  observations: z.string().min(10, "Please provide detailed observations"),
});

type AssessmentFormValues = z.infer<typeof assessmentFormSchema>;

export default function Performance() {
  const [selectedCycle, setSelectedCycle] = useState<ExtendedPerformanceCycle | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  const { data: cycles, isLoading } = useQuery(orpc.performanceCycles.list.queryOptions());

  const form = useForm<AssessmentFormValues>({
    resolver: zodResolver(assessmentFormSchema),
    defaultValues: {
      ratingFinal: "",
      observations: "",
    },
  });

  const submitAssessment = useMutation({
    mutationFn: async (data: AssessmentFormValues & { cycleId: string }) => {
      return rpcClient.performanceCycles.submitAssessment({
        id: data.cycleId,
        ratingFinal: data.ratingFinal,
        observations: data.observations,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.performanceCycles.list.queryOptions().queryKey,
      });
      setSelectedCycle(null);
      form.reset();
      toast({ title: "Assessment submitted successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to submit assessment",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: AssessmentFormValues) => {
    if (!selectedCycle) return;
    submitAssessment.mutate({ ...data, cycleId: selectedCycle.id });
  };

  const filteredCycles =
    cycles?.filter((cycle) => matchesSearch(cycle.employee?.fullName || "", searchQuery)) ?? [];

  const pendingCount = cycles?.filter((c) => c.status === "pending").length || 0;
  const completedCount = cycles?.filter((c) => c.status === "completed").length || 0;
  const meetsCount =
    cycles?.filter((c) => c.managerAssessments?.some((a) => a.ratingFinal === "meets")).length || 0;

  const columns = [
    {
      key: "employee",
      header: "Employee",
      cell: (cycle: ExtendedPerformanceCycle) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-[hsl(var(--comp-coral-mist))] text-[hsl(var(--comp-coral))] text-xs font-medium">
              {cycle.employee?.fullName
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase() || "??"}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{cycle.employee?.fullName || "Unknown"}</p>
            <p className="text-xs text-muted-foreground">{cycle.employee?.jobTitle || "-"}</p>
          </div>
        </div>
      ),
    },
    {
      key: "cycleType",
      header: "Type",
      cell: (cycle: ExtendedPerformanceCycle) => (
        <Badge variant="outline" className="text-xs">
          {cycle.cycleType === "first_3m" ? "3-Month" : "Annual"}
        </Badge>
      ),
    },
    {
      key: "cycleDate",
      header: "Review Date",
      cell: (cycle: ExtendedPerformanceCycle) => formatDate(cycle.cycleDate),
    },
    {
      key: "dueDate",
      header: "Due Date",
      cell: (cycle: ExtendedPerformanceCycle) => {
        const dueDate = parseLocalDate(cycle.dueDate);
        const now = new Date();
        const isOverdue = dueDate < now && cycle.status !== "completed";
        const isDueSoon =
          dueDate.getTime() - now.getTime() < 7 * 24 * 60 * 60 * 1000 &&
          cycle.status !== "completed";

        return (
          <span
            className={
              isOverdue
                ? "font-medium text-[hsl(var(--alert-red))]"
                : isDueSoon
                  ? "text-[hsl(var(--amber-state))]"
                  : ""
            }
          >
            {formatDate(cycle.dueDate)}
          </span>
        );
      },
    },
    {
      key: "rating",
      header: "Rating",
      cell: (cycle: ExtendedPerformanceCycle) => {
        const assessment = cycle.managerAssessments?.[0];
        if (!assessment) return "-";
        return <StatusBadge status={assessment.ratingFinal} />;
      },
    },
    {
      key: "status",
      header: "Status",
      cell: (cycle: ExtendedPerformanceCycle) => <StatusBadge status={cycle.status} />,
    },
    {
      key: "actions",
      header: "",
      cell: (cycle: ExtendedPerformanceCycle) =>
        cycle.status === "pending" && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCycle(cycle);
            }}
            data-testid={`button-assess-${cycle.id}`}
          >
            Submit Review
          </Button>
        ),
    },
  ];

  return (
    <div className="flex-1 space-y-6">
      <PageHeader title="Performance Reviews" description="Manage employee performance cycles" />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <Calendar className="h-5 w-5 text-[hsl(var(--amber-state))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{pendingCount}</p>
              <p className="text-sm text-muted-foreground">Pending Reviews</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <TrendingUp className="h-5 w-5 text-[hsl(var(--forest-pulse))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{meetsCount}</p>
              <p className="text-sm text-muted-foreground">Meets Expectations</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <TrendingDown className="h-5 w-5 text-[hsl(var(--ink-muted))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{completedCount}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedCycle} onOpenChange={(open) => !open && setSelectedCycle(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review: {selectedCycle?.employee?.fullName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/50 p-3">
              <p className="text-sm">
                <span className="font-medium">Review Type:</span>{" "}
                {selectedCycle?.cycleType === "first_3m" ? "3-Month Probation" : "Annual Review"}
              </p>
              <p className="text-sm mt-1">
                <span className="font-medium">Due Date:</span> {formatDate(selectedCycle?.dueDate)}
              </p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="ratingFinal"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rating</FormLabel>
                      <SearchableSelect
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        options={PERFORMANCE_RATINGS.map((rating) => ({
                          value: rating.value,
                          label: rating.label,
                        }))}
                        placeholder="Select rating"
                        data-testid="select-rating"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="observations"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Observations</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Provide detailed feedback on performance, achievements, and areas for improvement..."
                          className="min-h-[120px]"
                          {...field}
                          data-testid="input-observations"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setSelectedCycle(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitAssessment.isPending}
                    data-testid="button-submit-assessment"
                  >
                    {submitAssessment.isPending ? "Submitting..." : "Submit Review"}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search reviews..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-performance"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredCycles}
        isLoading={isLoading}
        emptyMessage="No performance cycles found"
        testIdPrefix="performance"
      />
    </div>
  );
}
